import { StringifyFunc, usePageContent } from "@robe/client/xcore-ui";
import { Box, Stack } from "@xcorejs/ui";
import { FormControl } from "design-system/xcore/Form";
import Select, { OptionType } from "design-system/xcore/Select";
import { OnChangeValue } from "react-select";
import { NewsPage, Product } from "xcore/types";
import { useRouter } from "next/router";
import { useLayout } from "xcore";
import { Filter, SupportedNewsQueryKeys } from "./types";
import { sentryLogger } from "@robe/core";
import { useEffect } from "react";

interface NewsFiltersProps {
  products: Filter;
  category: Filter;
  year: Filter;

  onChange: (key: SupportedNewsQueryKeys) => (value: OnChangeValue<OptionType, boolean>) => void;
}

const NewsFilters = ({ products, category, year, onChange }: NewsFiltersProps) => {
  const { locale } = useRouter();
  const { stringify } = useLayout();
  const content = usePageContent<NewsPage>();

  useEffect(() => {
    (locale === "en-SEA" || locale === "en-NA") &&
      sentryLogger({
        type: "debug",
        debugBlock: "filters",
        message: "news filters options",
        context: {
          category,
          products,
        },
      });
  }, []);

  if (!content.values) {
    return null;
  }

  return (
    <Stack direction={{ _: "column", sm: "row" }} gap={{ _: "1.5rem", sm: "3rem" }}>
      {[
        <Box width="100%" key="product">
          <FormControl labelText={stringify(content.values.productPlaceholder)}>
            <Select
              {...products?.[0]}
              onChange={onChange("product")}
              placeholder={stringify(content.values.realProductPlaceholder)}
              isMulti
            />
          </FormControl>
        </Box>,
        <Box width="100%" key="category">
          <FormControl labelText={stringify(content.values.categoryPlaceholder)}>
            <Select
              {...category[0]}
              onChange={(e) => {
                sentryLogger({
                  type: "debug",
                  message: "News category filter change",
                  debugBlock: "filters",
                  context: {
                    e,
                    category,
                    products,
                  },
                });

                onChange("category")(e);
              }}
              placeholder={stringify(content.values.realCategoryPlaceholder)}
              isMulti
            />
          </FormControl>
        </Box>,
        /* TODO: temporary change until its removal from Robe is requested */
        locale !== "fr" && (
          <Box width="100%" key="fr">
            <FormControl labelText={stringify(content.values.yearPlaceholder)}>
              <Select
                {...year[0]}
                onChange={(v) => onChange("year")([v as any])}
                placeholder={stringify(content.values.realYearPlaceholder)}
                isClearable
              />
            </FormControl>
          </Box>
        ),
      ].filter(Boolean)}
    </Stack>
  );
};

export default NewsFilters;

export const mapAndSortProductFilterOptions = (products: Product[], stringify: StringifyFunc) =>
  products
    .map((product) => ({
      label: stringify(product.title),
      value: product.id,
    }))
    .sort((x, y) => x.label.localeCompare(y.label));
